import { FC, useMemo } from 'react'
import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useNotificationsList } from 'services/notifications/notifications.hook'
import { NOTIFICATION_STATUS_TYPE } from 'services/notifications/notifications.consts'
import { useBenefitsList } from 'services/benefits/benefits.hooks'

const TABS = [
  {
    value: 'notifications',
    i18n: { id: 'notificationsPage.notifications', defaultMessage: 'Powiadomienia' }
  },
  {
    value: 'discounts',
    i18n: { id: 'discounts.zone', defaultMessage: 'Strefa rabatów' }
  }
]

// Toggle between Powiadomienia and Strefa rabatów ("Panel Klienta 3.0").
// Desktop: pill group with count bubbles. Mobile: segment control.
const NotificationsTabs: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const { notificationsList } = useNotificationsList()
  const { benefitsList = [] } = useBenefitsList()

  const currentTab = useMemo(() => {
    const path = location.pathname.split('/')[1]
    return TABS.some(({ value }) => value === path) ? path : 'notifications'
  }, [location.pathname])

  const counts: Record<string, number> = {
    notifications: notificationsList.filter(
      ({ status }) => status === NOTIFICATION_STATUS_TYPE.unread
    ).length,
    discounts: benefitsList.length
  }

  if (isDesktop) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          gap: '6px',
          backgroundColor: '#F9FAFB',
          padding: '4px'
        }}>
        {TABS.map(({ value, i18n }) => {
          const isActive = currentTab === value
          return (
            <ButtonBase
              key={value}
              onClick={() => navigate(`/${value}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 20px',
                backgroundColor: isActive ? theme.r4r.navy : 'transparent',
                color: isActive ? '#fff' : theme.r4r.grey,
                fontFamily: theme.typography.fontFamily,
                fontSize: '13px',
                fontWeight: isActive ? 700 : 500,
                whiteSpace: 'nowrap'
              }}>
              {formatMessage(i18n)}
              {counts[value] > 0 && (
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isActive ? alpha('#fff', 0.25) : alpha(theme.r4r.navy, 0.1),
                    color: isActive ? '#fff' : theme.r4r.navy,
                    fontSize: '10px',
                    fontWeight: 700
                  }}>
                  {counts[value]}
                </Box>
              )}
            </ButtonBase>
          )
        })}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: '320px',
        mx: 'auto',
        backgroundColor: theme.r4r.greyBg,
        padding: '2px'
      }}>
      {TABS.map(({ value, i18n }) => {
        const isActive = currentTab === value
        return (
          <ButtonBase
            key={value}
            onClick={() => navigate(`/${value}`)}
            sx={{
              flex: 1,
              padding: '5px 8px',
              backgroundColor: isActive ? theme.r4r.navy : 'transparent',
              boxShadow: isActive ? '0px 0px 5px 0px rgba(0, 0, 0, 0.16)' : 'none'
            }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '22px',
                whiteSpace: 'nowrap',
                color: isActive ? '#fff' : theme.r4r.grey
              }}>
              {formatMessage(i18n)}
            </Typography>
          </ButtonBase>
        )
      })}
    </Box>
  )
}

export default NotificationsTabs
